/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Cookies from "js-cookie";
import { createMessage } from "lib/api";
import { sendMessageEvent } from "lib/analytics";
import { Media } from "solar/components/Media";
import { useMediaQuery } from "react-responsive";
import { formatResponseTime } from "helpers/formatting";
import Modal from "components/shared/stays/Modal";
import ModalFooter from "components/shared/stays/ModalFooter";
import ModalBody from "components/shared/stays/ModalBody";
import Title from "components/shared/stays/Title";
import Button from "solar/components/Button";
import Photo from "components/shared/stays/Photo";
import Text from "components/shared/stays/Text";
import FormNote from "solar/components/FormNote";
import FormGroup from "components/auth/FormGroup";
import MessageInput from "components/shared/stays/MessageInput";
import RecommendedSmall from "components/stay/RecommendedSmall";
import styles from "components/shared/stays/MessageModal.module.scss";

export default function MessageModal({
  recipientName,
  show,
  setShow,
  tripID,
  tripName,
  guideID,
  guidePrimaryEmployeeImageUrl,
  responseTime = null,
  closestTrips = [],
}) {
  const [success, setSuccess] = useState(null);

  const greaterThanLg = useMediaQuery({
    query: "(min-width: 1024px)", // rt: should use a variable
  });

  const schema = Yup.object().shape({
    content: Yup.string().required("Please add a message for the guide."),
  });

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      trip_id: tripID,
      receiver_id: guideID,
      receiver_type: "Operator",
    },
    mode: "onChange",
  });

  // get trips that have already been messaged
  const messagedTrips = JSON.parse(Cookies.get("messaged-trips") || "[]");
  // get message sent to previous trips
  const savedMessage =
    messagedTrips.indexOf(tripID) < 0 ? Cookies.get("guide-message") : null;

  useEffect(() => {
    // immediataly set the saved message if applicable
    if (savedMessage) {
      setValue("content", savedMessage, { shouldValidate: true });
    }
  }, []);

  function handleModalClose() {
    setShow(false);
    setSuccess(null);
  }

  async function handleSendMessage(data) {
    const response = await createMessage(data);
    if (response.errorMsg) {
      // rt: is this needed? should this function be connected with the const above in a different way with the error state there
    } else {
      // save message text in cookie
      Cookies.set("guide-message", data.content);

      // add the id of trip to cookie
      const messagedArray = [...messagedTrips, tripID];
      const messagedSet = new Set(messagedArray);
      Cookies.set("messaged-trips", JSON.stringify([...messagedSet]));

      sendMessageEvent("modal");
      setSuccess(true);
    }
  }

  return (
    // eslint-disable-next-line react/jsx-no-bind
    <Modal show={show} handleClose={handleModalClose} className={styles.modal}>
      {success ? (
        <ModalBody>
          <Title size={4}>Thanks!</Title>
          <Text className={styles.text}>
            {`${recipientName} at ${tripName} has a 100% response rate. Expect to hear back within ${formatResponseTime(
              responseTime
            )}.`}
          </Text>

          {closestTrips && (
            <div className={styles.recommended}>
              <Title size={5}>Nearby trips</Title>
              <RecommendedSmall trips={closestTrips} />
            </div>
          )}
        </ModalBody>
      ) : (
        <>
          <ModalBody>
            <Media lessThan="md">
              <div className={styles.visual}>
                <Photo
                  src={guidePrimaryEmployeeImageUrl}
                  alt={recipientName}
                  layout="fill"
                  objectFit="cover"
                />
              </div>
            </Media>
            <Title size={4} className={styles.title}>
              {`Ask ${recipientName}`}
            </Title>
            <div className={styles.intro}>
              <div>
                <Text className={styles.text}>
                  Send a message to create a custom trip, book a private group
                  or just find out more about the surf.
                </Text>
              </div>
              <Media greaterThan="lg">
                <div className={styles.visual}>
                  <Photo
                    src={guidePrimaryEmployeeImageUrl}
                    alt={recipientName}
                    layout="fill"
                    objectFit="cover"
                  />
                </div>
              </Media>
            </div>
            <form
              id="message-form"
              onSubmit={handleSubmit(handleSendMessage)}
              noValidate
              className={styles.form}
            >
              <FormGroup>
                <MessageInput name="content" register={register} />
                {errors?.content && (
                  <FormNote hasError>{errors.content.message}</FormNote>
                )}
              </FormGroup>
              <input name="trip_id" type="hidden" {...register("trip_id")} />
              <input
                name="receiver_id"
                type="hidden"
                {...register("receiver_id")}
              />
              <input
                name="receiver_type"
                type="hidden"
                {...register("receiver_type")}
              />
            </form>
          </ModalBody>
          <ModalFooter bordered={!greaterThanLg}>
            <Button
              form="message-form"
              type="submit"
              variant="black"
              size={2}
              isSubmitting={isSubmitting}
              isDisabled={!isValid}
              responsive
              heap="message-send"
            >
              Send message
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}

MessageModal.propTypes = {
  recipientName: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
  guideID: PropTypes.string.isRequired,
  guidePrimaryEmployeeImageUrl: PropTypes.string.isRequired,
  tripID: PropTypes.string.isRequired,
  tripName: PropTypes.string.isRequired,
  responseTime: PropTypes.string,
  closestTrips: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      slug: PropTypes.string,
      country: PropTypes.string,
      lowest_price: PropTypes.node,
      product_type: PropTypes.string,
      trip_type_description: PropTypes.string,
      is_featured: PropTypes.bool,
      image_url: PropTypes.string,
      distance: PropTypes.number,
    })
  ),
};
